<template>
  <div class="container section-p70 groups-page-container">
    <div id="titleRow" class="row">
      <div class="col">
        <title-with-results :title="t('TRAINING_GROUPS')"
                            :number-of-results="adminAvailableGroupsRef?.numberOfResults()"
                            v-if="showAdminAvailableGroups"
        />
        <div v-else>
          <h2 class="title-blue mb-3">{{ t("TRAINING_GROUPS") }}</h2>

          <template v-if="isTraineeOrAdminAsTrainee && accountData.id">
            <Card class="mb-3">
              <template #content>
                <p class="text16-blue mb-3 pb-3 border-bottom" v-if="isAdminAsTrainee">
                  <span class="me-1">{{ t("SSN") }}:</span>
                  <router-link :to="{name: 'Trainee', params: { id: `${accountData.id}` } }" class="text16-blue">
                    {{ accountData.ssn }}
                  </router-link>
                </p>

                <div v-if="isEligible !== null">
                  <div class="text24-blue">
                    {{ t("STATUS") }}:&nbsp;{{ t(isEligible ? "ELIGIBLE" : "NOT_ELIGIBLE").toLowerCase() }}
                  </div>

                  <p class="text16-blue mb-0">
                    {{ t(isEligible ? "ELIGIBLE_INFO_TEXT" : "NOT_ELIGIBLE_INFO_TEXT") }}
                  </p>
                </div>

                <p class="mt-3 mb-0 text-danger" v-if="accountData.unconfirmedEmail && isTrainee">
                  <i class="pi pi-exclamation-triangle me-2"></i>
                  <span v-html="t('ERRORS.CONFIRM_EMAIL_TO_REGISTER_TO_TRAINING_GROUPS') + ':'"></span>
                  <router-link :to="{ name: 'UserProfile' }" class="ms-1 text-danger fw-bold">
                    {{ t("MANAGE_PROFILE").toLowerCase() }}.
                  </router-link>
                </p>
              </template>
            </Card>
          </template>
        </div>
      </div>
    </div>

    <div v-if="isTraineeOrAdminAsTrainee" class="row">
      <div class="col-12">
        <div class="text24-blue">
          {{ t("REGISTERED") }}
        </div>
      </div>
      <div class="col-12 mt-3">
        <div class="groups-table">
          <user-table
              :items="userActiveGroups"
              @request-cancellation="openCancellationModal"
          ></user-table>
        </div>
      </div>
    </div>

    <form v-if="showAdminAvailableGroups" @submit.prevent="doSearch" class="row">
      <div class="col">
        <div class="row align-items-center mt-3">
          <div class="col-lg-6 col-xl-4 mb-2">
            <InputText
                id="keyword"
                v-model="keyword"
                :placeholder="t('FILTER_GROUPS')"
                class="w-100"
                type="text"
            />
          </div>

          <div class="col-lg-6 col-xl-4 mb-2">
            <Select
                label-id="searchStatus"
                v-model="searchStatus"
                :options="statusOptions"
                option-label="text"
                option-value="value"
                class="w-100"
            />
          </div>

          <div class="col-auto mb-2">
            <Button type="submit" :label="t('SEARCH')"/>
          </div>

          <div class="col-12 mt-3">
            <div class="groups-table">
              <available-groups-table
                  :filterStatus="searchStatus"
                  :filterText="keyword"
                  ref="adminAvailableGroupsRef"
              />
            </div>
          </div>
        </div>
      </div>
    </form>

    <div v-if="isTraineeOrAdminAsTrainee" class="row">
      <div class="col-12">
        <div class="text24-blue">
          {{ t("UPCOMING") }}
        </div>
      </div>
      <div class="col-12 mt-3">
        <div class="row justify-content-between">
          <div class="col-xl-3 col-lg-6 mb-2">
            <InputText
                id="keyword-upcoming"
                v-model="keyword"
                :placeholder="t('FILTER_GROUPS')"
                class="w-100"
                type="text"
            />
          </div>

          <div class="col-auto text18-blue bold align-self-center text-lg-right mb-2">
            <span class="me-2">{{ t("EXPLANATION") }}</span>
            <router-link to="/programme#section-faq" class="text-nowrap">{{ t("READ_MORE") }}</router-link>
          </div>
        </div>
      </div>
      <div class="col-12 mt-3">
        <div class="groups-table">
          <user-table
              :filterStatus="searchStatus"
              :filterText="keyword"
              :items="availableGroupsToDisplay"
              :register="true"
              @request-registration="openRegistrationModal"
          ></user-table>
        </div>
      </div>
    </div>

    <language-modal :isModalVisible="isModalVisible"></language-modal>
    <confirm-registration
        ref="confirmRegistrationRef"
        @handleSuccess="handleRegistrationSuccess"
    ></confirm-registration>
    <cancel-training-registration-modal
        ref="cancelRegistrationRef"
        @handleCancel="handleRegistrationSuccess"
    ></cancel-training-registration-modal>

    <div class="row" v-if="isTraineeOrAdminAsTrainee">
      <div class="col-12">
        <div class="text24-blue">
          {{ t("ENDED_GROUPS") }}
        </div>
      </div>
      <div class="col-12 mt-3">
        <div class="groups-table">
          <user-table
              :items="userArchivedGroups"
              :show-result="true"
              @request-cancellation="openCancellationModal"
          ></user-table>
        </div>
      </div>
    </div>

    <div class="row" v-if="isTraineeOrAdminAsTrainee">
      <div class="col-12">
        <div class="text24-blue">
          {{ t("OTHER_LANGUAGE_TRAINING") }}
        </div>
      </div>
      <div class="col-12 mt-3">
        <PaginatedTable id="paginated-trainingGroups"
                        :fixed-rows-per-page="15"
                        :on-page-change="fetchInstitutionDiplomas"
                        :fields="institutionDiplomaFields"
                        :small="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import availableGroupsTable from "@/views/components/tables/administrativeGroupsTable.vue";
import userTable from "@/views/components/tables/userTable.vue";
import {computed, onMounted, ref, watch} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import languageModal from "@/views/components/modals/languageModal.vue";
import CancelTrainingRegistrationModal from "@/views/components/modals/cancelTrainingRegistrationModal.vue";
import ConfirmRegistration from "@/views/components/modals/confirmRegistration.vue";
import {useRoute, useRouter} from "vue-router";
import {Role} from "@/auth/oauth";
import {DiplomaStatus} from "@/model/institution-diploma";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import TitleWithResults from "@/components/TitleWithResults.vue";
import {GroupStatus} from "@/model/group-status";

const auth = injectAuth();
const axios = injectAxios();
const {t} = useI18n();
const route = useRoute();
const router = useRouter();

const isEligible = ref(null);
const keyword = ref(route.query.searchValue?.toString());
const searchStatus = ref(route.query.status ?? GroupStatus.PUBLIC);
const isModalVisible = ref(false);
const registeredGroups = ref([]);
const availableGroups = ref([]);
const traineeRegisteredGroups = ref([]);
const traineeAvailableGroups = ref([]);
const availableGroupsForStudent = ref([]);
const accountData = ref({});
const studentId = ref(route.params.studentId || null);
const adminAvailableGroupsRef = ref(null);
const confirmRegistrationRef = ref(null);
const cancelRegistrationRef = ref(null);

const isAdmin = computed(() => {
  return (
      auth.isAuthenticated() &&
      (auth.hasRole("ADMIN") ||
          auth.hasRole("SCHOOL_MANAGER") ||
          auth.hasRole("TRAINER") ||
          auth.hasRole("VIEWER"))
  );
});

const isTrainee = computed(() => {
  return auth.isAuthenticated() && auth.hasRole("TRAINEE");
});

const isAdminAsTrainee = computed(() => {
  return isAdmin.value && studentId.value;
});

const isTraineeOrAdminAsTrainee = computed(() => {
  return isTrainee.value || isAdminAsTrainee.value;
});

const showAdminAvailableGroups = computed(() => {
  return isAdmin.value && !isAdminAsTrainee.value;
});

const statusOptions = computed(() => {
  let result = [
    {value: GroupStatus.DRAFT, text: t(GroupStatus.DRAFT)},
    {value: GroupStatus.PRIVATE, text: t(GroupStatus.PRIVATE)},
    {value: GroupStatus.PUBLIC, text: t(GroupStatus.PUBLIC)},
    {value: GroupStatus.ALL_GROUP_STATUSES, text: t(GroupStatus.ALL_GROUP_STATUSES)},
  ];

  if (!auth.hasRole(Role.TRAINER)) {
    result = [{value: GroupStatus.ARCHIVED, text: t(GroupStatus.ARCHIVED)}, ...result];
  }

  return result;
});

const institutionDiplomaFields = computed(() => {
  return [
    {key: "diplomaLevel", label: t("LANGUAGE_DIPLOMA_LEVEL"), sortable: true},
    {key: "institutionName", label: t("INSTITUTION"), sortable: true},
    {key: "documentNumber", label: t("DOCUMENT_NUMBER"), sortable: true},
  ];
});

async function fetchGroups() {
  if (studentId.value) {
    // Administrator views student groups
    await Promise.all([
      fetchStudentGroups(studentId.value),
      getAvailableGroupsForStudent(studentId.value)
    ]);
  } else if (auth.hasRole(Role.TRAINEE)) {
    // Trainee views their own groups
    await Promise.all([
      getRegisteredGroups(),
      getAvailableGroups()
    ]);
  }
}

function handleRegistrationSuccess() {
  getRegisteredGroups();
  getAvailableGroups();
}

function openRegistrationModal(group) {
  confirmRegistrationRef.value?.showRegistrationModal(group);
}

function openCancellationModal(group) {
  cancelRegistrationRef.value?.showRegistrationCancellationModal(group);
}

async function fetchStudentGroups(sId) {
  const resp = await axios.get(`/account/student_groups/${sId}`);
  traineeRegisteredGroups.value = resp.data;
  traineeAvailableGroups.value = resp.data;
}

const userActiveGroups = computed(() => {
  const groupsList = isAdminAsTrainee.value
      ? traineeRegisteredGroups.value
      : registeredGroups.value;
  return groupsList
      ? groupsList.filter((group) => group.status !== "ARCHIVED")
      : [];
});

const userArchivedGroups = computed(() => {
  const groupsList = isAdminAsTrainee.value
      ? traineeRegisteredGroups.value
      : registeredGroups.value;
  return groupsList
      ? groupsList.filter((group) => group.status === "ARCHIVED")
      : [];
});

const availableGroupsToDisplay = computed(() => {
  return isAdminAsTrainee.value
      ? availableGroupsForStudent.value
      : availableGroups.value;
});

const fetchAccountData = async () => {
  const resp = await axios.get(isAdminAsTrainee.value ? `/account/${studentId.value}` : "/account");
  accountData.value = resp.data;
};

onMounted(() => {
  fetchGroups();
  fetchAccountData();
});

watch(
    () => route.params.studentId,
    (newStudentId) => {
      studentId.value = newStudentId || null;
      fetchGroups();
    }
);

const getRegisteredGroups = async () => {
  const resp = await axios.get("/account/groups");
  registeredGroups.value = resp.data;
};

const getAvailableGroups = async () => {
  const resp = await axios.get("/training/groups/foreknown");

  isEligible.value = resp.data.eligible;
  availableGroups.value = isEligible.value ? resp.data.groups : null;

  if (isEligible.value) {
    showLanguageModalIfNecessary();
  }
};

const getAvailableGroupsForStudent = async (studentId) => {
  const resp = await axios.get(`/training/groups/foreknown_student/${studentId}`);

  isEligible.value = resp.data.eligible;
  availableGroupsForStudent.value = isEligible.value ? resp.data.groups : null;
};

const doSearch = async (event) => {
  event.preventDefault();
  adminAvailableGroupsRef.value.resetGroupsTable();
};

const canShowLanguageModalToUser = () => {
  return isTrainee.value && (auth.getRVK() || auth.getTemp()) && isEligible.value;
};

const showLanguageModalIfNecessary = () => {
  if (!canShowLanguageModalToUser()) {
    return;
  }

  if (!["/login", "/oauth"].includes(router.options.history.state?.back?.split("?")[0])) {
    return;
  }

  const maxModalShowCount = 5;
  const modalShowCountStorageKey = "language-modal-show-count__" + auth.getAcc();

  let modalShowCount = Number.parseInt(localStorage.getItem(modalShowCountStorageKey) || "0");
  if (Number.isNaN(modalShowCount)) {
    modalShowCount = 0;
  }

  if (modalShowCount >= maxModalShowCount) {
    return;
  }

  localStorage.setItem(modalShowCountStorageKey, `${modalShowCount + 1}`);
  isModalVisible.value = true;
};

const fetchInstitutionDiplomas = async (data) => {
  const params = {
    ...data,
    status: DiplomaStatus.APPROVED
  };

  const accountId = studentId.value || auth.getAcc();
  return await axios.get(`/account/${accountId}/institutions/diplomas`, {params: params});
};
</script>

<style lang="less" scoped>
.groups-table {
  text-align: left;
}

.separator-margin {
  margin: 2rem 0;
}
</style>
